import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import toast from 'react-hot-toast';
import { adminQuotesApi } from '../services/adminApi';
import DataTable from './components/DataTable';
import Modal from './components/Modal';
import Badge from './components/Badge';
import { QUOTE_STATUS_TONES, QUOTE_STATUSES } from './statusTones';

interface Quote {
  id: string;
  customer_name: string;
  customer_email: string;
  status: string;
  created_at: string;
  message?: string;
}

const fmt = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

interface Props {
  onLogout: () => void;
  onCountChange?: (count: number) => void;
}

const AdminQuotes: React.FC<Props> = ({ onLogout, onCountChange }) => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [notes, setNotes] = useState('');

  const fetchQuotes = useCallback(async () => {
    try {
      const data = await adminQuotesApi.getAll();
      setQuotes(data);
      onCountChange?.(data.length);
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') onLogout();
      else toast.error('Impossible de charger les devis');
    } finally {
      setLoading(false);
    }
  }, [onLogout, onCountChange]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const handleStatus = async (id: string, status: string) => {
    try {
      await adminQuotesApi.updateStatus(id, status, notes || undefined);
      toast.success(`Devis mis à jour : ${status}`);
      setSelectedQuote(null);
      setNotes('');
      fetchQuotes();
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') { onLogout(); return; }
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const columns = useMemo<ColumnDef<Quote, any>[]>(() => [
    {
      accessorKey: 'customer_name',
      header: 'Client',
      cell: (info) => <div className="font-medium text-coffee-200">{info.getValue() as string}</div>,
    },
    {
      accessorKey: 'customer_email',
      header: 'Email',
      cell: (info) => <div className="text-coffee-100">{info.getValue() as string}</div>,
    },
    {
      accessorKey: 'created_at',
      header: 'Date',
      cell: (info) => <span className="text-coffee-100">{fmt(info.getValue() as string)}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Statut',
      cell: (info) => <Badge tone={QUOTE_STATUS_TONES[info.getValue() as string] ?? 'gray'}>{info.getValue() as string}</Badge>,
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: (info) => (
        <button
          onClick={() => { setSelectedQuote(info.row.original); setNotes(''); }}
          className="rounded-lg bg-forest-200 px-3 py-1.5 text-xs font-semibold text-cream-50 transition-colors hover:bg-forest-100"
        >
          Gérer
        </button>
      ),
    },
  ], []);

  return (
    <div>
      <DataTable
        columns={columns}
        data={quotes}
        loading={loading}
        emptyMessage="Aucun devis"
        searchPlaceholder="Rechercher un devis..."
      />

      {selectedQuote && (
        <Modal onClose={() => setSelectedQuote(null)}>
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-coffee-200">{selectedQuote.customer_name}</h2>
              <p className="text-sm text-coffee-100">{selectedQuote.customer_email}</p>
            </div>
            <Badge tone={QUOTE_STATUS_TONES[selectedQuote.status] ?? 'gray'}>{selectedQuote.status}</Badge>
          </div>

          {selectedQuote.message && (
            <div className="mb-4 rounded-lg bg-cream-100 p-3 text-sm text-coffee-100">
              <span className="font-medium text-coffee-200">Message : </span>{selectedQuote.message}
            </div>
          )}

          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-coffee-200">Notes admin (optionnel)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full resize-none rounded-lg border border-cream-200 bg-cream-50 px-3 py-2 text-sm"
              placeholder="Ajouter une note..."
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            {QUOTE_STATUSES.filter((s) => s !== selectedQuote.status).map((s) => (
              <button
                key={s}
                onClick={() => handleStatus(selectedQuote.id, s)}
                className={`rounded-lg px-3 py-2 text-sm font-medium capitalize transition-colors ${
                  s === 'accepted' ? 'bg-green-600 text-white hover:bg-green-700'
                  : s === 'rejected' ? 'bg-red-500 text-white hover:bg-red-600'
                  : s === 'quoted' ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-cream-200 text-coffee-200 hover:bg-cream-300'
                }`}
              >
                → {s}
              </button>
            ))}
          </div>

          <button onClick={() => setSelectedQuote(null)} className="mt-4 w-full rounded-lg border border-cream-200 py-2 text-sm text-coffee-100 hover:bg-cream-100">
            Fermer
          </button>
        </Modal>
      )}
    </div>
  );
};

export default AdminQuotes;
